import { Component, Inject, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators, FormArray } from '@angular/forms';
import { FeatureFlagsAdminService } from '../../services/feature-flags-admin.service';
import { FeatureFlag, Rule, BooleanRule, GroupRule, BaseRule } from '../../../models/feature-flag.model';
import { of } from 'rxjs';
import { catchError, filter, finalize } from 'rxjs/operators';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

/**
 * Component for creating and editing feature flags
 */
@Component({
  selector: 'cbre-feature-flag-form',
  templateUrl: './feature-flag-form.component.html',
  styleUrls: ['./feature-flag-form.component.scss'],
  standalone: false,
})
export class FeatureFlagFormComponent implements OnInit {
  /**
   * ID of the feature flag to edit (null for create mode)
   */
  @Input() flagId: string = null;

  /**
   * Whether to show advanced rule builder
   */
  @Input() advancedRuleBuilder = true;

  /**
   * Event emitted when the form is submitted successfully
   */
  @Output() formSubmitted = new EventEmitter<string>();

  /**
   * Event emitted when the form is cancelled
   */
  @Output() formCancelled = new EventEmitter<void>();

  /**
   * Form group for the feature flag form
   */
  flagForm: FormGroup;

  /**
   * Original feature flag data (for edit mode)
   */
  originalFlag: FeatureFlag;

  /**
   * Whether the form is in loading state
   */
  loading = false;

  /**
   * Whether the form is in submitting state
   */
  submitting = false;

  /**
   * Error message
   */
  errorMessage: string = null;

  /**
   * Selected rule type
   */
  selectedRuleType: 'boolean' | 'email' | 'userGroup' | 'region' | 'lineOfBusiness' | 'userRole' | 'group' = 'boolean';

  /**
   * Available rule types
   */
  ruleTypes = [
    { value: 'boolean', label: 'Boolean' },
    { value: 'email', label: 'Email' },
    { value: 'userGroup', label: 'User Group' },
    { value: 'userRole', label: 'User Role' },
    { value: 'region', label: 'Region' },
    { value: 'lineOfBusiness', label: 'Line of Business' },
    { value: 'group', label: 'Group (Advanced)' },
  ];

  /**
   * Feature flag ID validation pattern
   * Alphanumeric, hyphens, and underscores only
   */
  idPattern = /^[a-zA-Z0-9_-]+$/;

  /**
   * Form control for the rule type dropdown
   */
  ruleTypeControl = new FormControl(null, [Validators.required]);

  /**
   * Whether the form is in edit mode
   */
  get isEditMode(): boolean {
    return !!this.flagId;
  }

  constructor(
    private fb: FormBuilder,
    private featureFlagsService: FeatureFlagsAdminService,
    @Inject(CbreLoggerToken) private logger: CbreLogger
  ) {}

  ngOnInit(): void {
    this.initForm();

    if (this.isEditMode) {
      this.loadFeatureFlag();
    } else {
      // Initialize with default boolean rule for create mode
      this.createDefaultBooleanRule();
    }

    // Sync rule type control with selectedRuleType
    this.ruleTypeControl.valueChanges.subscribe(value => (this.selectedRuleType = value as never));
  }

  /**
   * Initialize the form
   */
  initForm(): void {
    this.flagForm = this.fb.group({
      id: ['', [Validators.required, Validators.pattern(this.idPattern)]],
      name: ['', Validators.required],
      description: [''],
      rule: this.fb.group({
        type: ['boolean'],
        value: [false],
      }),
    });

    // Disable ID field in edit mode
    if (this.isEditMode) {
      this.flagForm.get('id').disable();
    }
  }

  /**
   * Create default boolean rule
   */
  createDefaultBooleanRule(): void {
    const defaultRule: BooleanRule = {
      type: 'boolean',
      value: false,
    };

    this.selectedRuleType = 'boolean';
    this.updateRuleFormGroup(defaultRule);
  }

  /**
   * Load feature flag for editing
   */
  loadFeatureFlag(): void {
    this.loading = true;
    this.errorMessage = null;

    this.featureFlagsService
      .getFeatureFlag(this.flagId)
      .pipe(
        catchError(error => {
          this.errorMessage = `Failed to load feature flag: ${error.message}`;
          return of(null);
        }),
        filter(Boolean),
        finalize(() => (this.loading = false))
      )
      .subscribe(flag => {
        this.originalFlag = flag;
        this.populateForm(flag);
      });
  }

  /**
   * Populate form with feature flag data
   */
  populateForm(flag: FeatureFlag): void {
    this.flagForm.patchValue({
      id: flag.id,
      name: flag.name,
      description: flag.description || '',
    });

    // Initialize the rule form group based on the existing rule
    this.updateRuleFormGroup(flag.rule);
  }

  /**
   * Update the rule form group based on the rule type
   */
  private updateRuleFormGroup(rule: Rule): void {
    // Update the selected rule type
    this.selectedRuleType = rule.type;
    this.ruleTypeControl.setValue(rule.type); // Ensure the Rule Type dropdown reflects the correct type

    const formGroup = this.flagForm.get('rule') as FormGroup;
    switch (rule.type) {
      case 'boolean':
        formGroup.setControl('type', this.fb.control('boolean'));
        formGroup.setControl('value', this.fb.control((rule as BooleanRule).value));
        break;
      case 'email':
      case 'userGroup':
      case 'region':
      case 'lineOfBusiness':
      case 'userRole':
        formGroup.setControl('type', this.fb.control(rule.type));
        formGroup.setControl('values', this.fb.array((rule.values || []).map(value => this.fb.control(value))));
        break;
      case 'group':
        formGroup.setControl('type', this.fb.control('group'));
        formGroup.setControl('operator', this.fb.control(rule['operator'] || 'AND'));
        const childRulesFormArray = this.fb.array(
          (rule['childRules'] || []).map(childRule => this.createRuleFormGroup(childRule))
        );
        formGroup.setControl('childRules', childRulesFormArray);
        break;
    }
  }

  /**
   * Add a value to the values FormArray
   */
  addValue(): void {
    const valuesArray = this.flagForm.get('rule.values') as FormArray;
    valuesArray.push(this.fb.control(''));
  }

  /**
   * Remove a value from the values FormArray
   */
  removeValue(index: number): void {
    const valuesArray = this.flagForm.get('rule.values') as FormArray;
    valuesArray.removeAt(index);
  }

  /**
   * Get the values FormArray
   */
  get valuesArray(): FormArray {
    return this.flagForm.get('rule.values') as FormArray;
  }

  /**
   * Handle rule type change
   */
  onRuleTypeChange(type: string): void {
    this.selectedRuleType = type as any;

    // Initialize default rule structure for the selected type
    const newRule: any = { type };

    switch (type) {
      case 'boolean':
        newRule.value = false;
        break;
      case 'email':
      case 'userGroup':
      case 'region':
      case 'lineOfBusiness':
      case 'userRole':
        newRule.values = [];
        break;
      case 'group':
        newRule.operator = 'AND';
        newRule.childRules = [];
        break;
    }

    this.updateRuleFormGroup(newRule);
  }

  /**
   * Submit the form
   */
  onSubmit(): void {
    if (this.flagForm.invalid) {
      // Mark all fields as touched to trigger validation messages
      Object.keys(this.flagForm.controls).forEach(key => {
        const control = this.flagForm.get(key);
        control.markAsTouched();
        control.markAsDirty();
      });
      return;
    }

    this.submitting = true;
    this.errorMessage = null;

    const formData = this.prepareFormData();

    const request$ = this.isEditMode
      ? this.featureFlagsService.updateFeatureFlag(this.flagId, formData)
      : this.featureFlagsService.createFeatureFlag(formData);

    request$
      .pipe(
        catchError(error => {
          this.errorMessage = `Failed to ${this.isEditMode ? 'update' : 'create'} feature flag: ${error.message}`;
          return of(null);
        }),
        finalize(() => {
          this.submitting = false;
        })
      )
      .subscribe(flag => {
        if (flag) {
          this.formSubmitted.emit(flag.id);
        }
      });
  }

  /**
   * Prepare form data for submission
   */
  prepareFormData(): any {
    const formValue = this.flagForm.getRawValue();

    return {
      id: formValue.id,
      name: formValue.name,
      description: formValue.description || '',
      rule: formValue.rule,
    };
  }

  /**
   * Cancel form
   */
  onCancel(): void {
    this.formCancelled.emit();
  }

  /**
   * Check if a field has validation errors
   */
  hasError(field: string, errorType: string = null): boolean {
    const control = this.flagForm.get(field);
    if (!control) return false;

    return control.touched && control.dirty && (errorType ? control.hasError(errorType) : control.invalid);
  }

  /**
   * Create a form group for a rule
   */
  private createRuleFormGroup(rule: Rule): FormGroup {
    switch (rule.type) {
      case 'boolean':
        return this.fb.group({
          type: [rule.type as string],
          value: [(rule as BooleanRule).value],
        });
      case 'email':
      case 'userGroup':
      case 'region':
      case 'lineOfBusiness':
      case 'userRole':
        return this.fb.group({
          type: [rule.type],
          values: this.fb.array((rule.values || []).map(value => this.fb.control(value))),
        });
      case 'group':
        return this.fb.group({
          type: [rule.type],
          operator: [rule['operator'] || 'AND'],
          childRules: this.fb.array((rule['childRules'] || []).map(childRule => this.createRuleFormGroup(childRule))),
        });
      default:
        const unsupportedRule = rule as BaseRule;
        this.logger.error(`Unsupported rule type: ${unsupportedRule.type}`);
        return this.fb.group({
          type: [unsupportedRule.type],
          value: [null],
        });
    }
  }

  /**
   * Add a nested group rule
   */
  addNestedGroup(): void {
    const childRulesArray = this.flagForm.get('rule.childRules') as FormArray;
    const newGroupRule: GroupRule = {
      type: 'group',
      operator: 'AND',
      childRules: [],
    };
    childRulesArray.push(this.createRuleFormGroup(newGroupRule));
  }

  /**
   * Remove a child rule
   */
  removeChildRule(index: number): void {
    const childRulesArray = this.flagForm.get('rule.childRules') as FormArray;
    childRulesArray.removeAt(index);
  }
}
